/**
 * Hotspot layer renderer — pulsing circles from analysis:hotspot escalation events.
 *
 * Renders HotspotEscalation results as concentric rings with escalation-level colors:
 * - score >= 70: red (critical escalation)
 * - score >= 40: orange (elevated)
 * - score < 40: amber (watch)
 *
 * Outer ring pulses using a time-varying radius to draw attention.
 * Inner ring shows the base position. Label shows hotspot name.
 */

import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { HotspotEscalation } from '@/intelligence/analysis/types';
import { eventBus } from '@/core/events';

/** Layer IDs handled by this renderer. */
export const HOTSPOT_LAYER_IDS = new Set(['hotspots']);

// ---------------------------------------------------------------------------
// Internal state — populated from analysis:hotspot events
// ---------------------------------------------------------------------------

/** Current hotspot escalations, keyed by hotspotId. Replaced each analysis cycle. */
const hotspotCache = new Map<string, HotspotEscalation>();

/** Cleanup function for event bus subscription. */
let cleanupFn: (() => void) | null = null;

/** App start timestamp for startup grace period. */
const APP_START_TIME = Date.now();

/** Delay before pulse animations activate (ms). */
const STARTUP_GRACE_MS = 60_000;

/** Subscribe to analysis:hotspot events from the analysis engine. */
export function initHotspotLayer(): void {
  if (cleanupFn) return; // Already initialized

  // Listen for the full analysis:complete result which includes all hotspots
  cleanupFn = eventBus.on('analysis:complete', (result) => {
    // Replace entire cache with latest escalations
    hotspotCache.clear();
    for (const esc of result.hotspotEscalations) {
      hotspotCache.set(esc.hotspotId, esc);
    }
  });
}

/** Tear down the hotspot layer event listener. */
export function destroyHotspotLayer(): void {
  if (cleanupFn) {
    cleanupFn();
    cleanupFn = null;
  }
  hotspotCache.clear();
}

// ---------------------------------------------------------------------------
// Color mapping
// ---------------------------------------------------------------------------

/**
 * Escalation fill with WM's alpha-carrying table (DeckGLMap.ts:4339-4345):
 * red/orange tiers at alpha 200, yellow at 180, all scaled by the zoom
 * baseOpacity so the fill itself carries the fade — no layer opacity on top.
 * Sentinel scores are 0-100; WM's 1-5 escalation scale maps via /20.
 */
export function getEscalationFill(score: number, baseOpacity: number): [number, number, number, number] {
  if (score >= 60) return [255, 68, 68, Math.round(200 * baseOpacity)];
  if (score >= 30) return [255, 165, 0, Math.round(200 * baseOpacity)];
  return [255, 255, 0, Math.round(180 * baseOpacity)];
}

/**
 * Marker radius in meters (WM DeckGLMap.ts:4335-4338): 10000 + score * 5000
 * on the 1-5 scale — Sentinel's 0-100 score divides by 20 to land on it.
 */
export function getEscalationRadius(score: number): number {
  return 10_000 + (score / 20) * 5000;
}

// ---------------------------------------------------------------------------
// Layer creation
// ---------------------------------------------------------------------------

/** Create deck.gl layers for hotspot escalation data. */
export function createHotspotLayers(zoom: number): Layer[] {
  const escalations = Array.from(hotspotCache.values());
  if (escalations.length === 0) return [];

  const layers: Layer[] = [];
  // Zoom-dependent base opacity + max-pixel ramp — WM DeckGLMap.ts:4325-4328
  const baseOpacity = zoom < 2.5 ? 0.5 : zoom < 4 ? 0.7 : 1.0;
  const maxPx = 6 + Math.round(14 * Math.min(1, (zoom - 1) / 3));

  // Pulsing outer glow — matching WorldMonitor pulse timing
  // 60s startup grace period before pulse activates
  const now = Date.now();
  const startupElapsed = now - APP_START_TIME;
  const pulseTime = startupElapsed > STARTUP_GRACE_MS ? now : 0;
  const pulse = pulseTime > 0
    ? 1.0 + 0.8 * (0.5 + 0.5 * Math.sin(pulseTime / 400))
    : 1.0;

  // Only pulse high/breaking escalations (score >= 60) — matching WorldMonitor
  const pulseEscalations = escalations.filter(e => e.score >= 60);
  if (pulseEscalations.length > 0) {
    layers.push(new ScatterplotLayer<HotspotEscalation>({
      id: 'hotspot-pulse',
      data: pulseEscalations,
      pickable: false,
      filled: false,
      stroked: true,
      radiusUnits: 'meters' as const,
      getPosition: (d) => [d.lng, d.lat],
      // WM's ring colors (DeckGLMap.ts:4371-4374): red for breaking, else orange
      getLineColor: (d) => {
        const raw = d as unknown as Record<string, unknown>;
        const a = Math.round(120 * baseOpacity);
        return raw.hasBreaking
          ? [255, 50, 50, a] as [number, number, number, number]
          : [255, 165, 0, a] as [number, number, number, number];
      },
      // Ring scales with escalation like the core dot (WM DeckGLMap.ts:4362-4365)
      getRadius: (d) => getEscalationRadius(d.score),
      radiusScale: pulse,
      radiusMinPixels: 6,
      radiusMaxPixels: 30,
      lineWidthMinPixels: 1.5,
      updateTriggers: {
        radiusScale: [now],
      },
    }));
  }

  // Core dot — WM sizing in meters, alpha carried in the fill (no layer opacity)
  layers.push(new ScatterplotLayer<HotspotEscalation>({
    id: 'hotspot-core',
    data: escalations,
    pickable: true,
    filled: true,
    stroked: true,
    lineWidthUnits: 'pixels' as const,
    lineWidthMinPixels: 2,
    radiusUnits: 'meters' as const,
    getPosition: (d) => [d.lng, d.lat],
    getFillColor: (d) => getEscalationFill(d.score, baseOpacity),
    getLineColor: (d) => {
      // White stroke only on breaking hotspots, matching WorldMonitor.
      // hasBreaking is not yet populated by the analysis engine (follow-up
      // in the plan doc) — the branch is kept so wiring it is one field.
      const raw = d as unknown as Record<string, unknown>;
      return raw.hasBreaking ? [255, 255, 255, 255] : [0, 0, 0, 0];
    },
    getRadius: (d) => getEscalationRadius(d.score),
    radiusMinPixels: 4,
    radiusMaxPixels: maxPx,
    updateTriggers: {
      getFillColor: [escalations.length, baseOpacity],
      getRadius: [escalations.length],
    },
  }));

  // Labels at higher zoom
  if (zoom >= 4) {
    layers.push(new TextLayer<HotspotEscalation>({
      id: 'hotspot-labels',
      data: escalations,
      getPosition: (d) => [d.lng, d.lat],
      getText: (d) => `${d.hotspotName} (${Math.round(d.score)})`,
      getSize: 10,
      getColor: [255, 255, 255, 200],
      getTextAnchor: 'start' as const,
      getAlignmentBaseline: 'center' as const,
      getPixelOffset: [14, 0],
      fontFamily: 'monospace',
      sizeUnits: 'pixels' as const,
      billboard: true,
      outlineWidth: 2,
      outlineColor: [0, 0, 0, 180],
    }));
  }

  return layers;
}
